import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { RateLimitPresets } from '../utils/rateLimiter';

const AuthContext = createContext();
const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:8080';

// ✅ FIX: Rate limiters for auth operations
const signupRateLimiter = RateLimitPresets.auth();
const loginRateLimiter = RateLimitPresets.auth();
const googleSignInRateLimiter = RateLimitPresets.auth();

// Export AuthContext for testing
export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false); // ✅ FIX: Track auth operation in progress
  const creatingProfileRef = useRef(false); // Track profile creation in progress
  const isMountedRef = useRef(true); // ✅ FIX: Track component mount status to prevent state updates on unmounted component
  const previousEmailVerifiedRef = useRef(null); // Track previous emailVerified state for proactive token refresh

  // ✅ FIX: Retry utility with exponential backoff for network failures
  const retryWithBackoff = async (fn, maxRetries = 3) => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        // Don't retry AUTH_FAILED or 409 conflicts
        if (error.message === 'AUTH_FAILED' || error.status === 409) {
          throw error;
        }

        // Don't retry if it's the last attempt
        if (attempt === maxRetries - 1) {
          throw error;
        }

        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };

  // Helper to get Firebase ID token
  // ✅ FIX: Add forceRefresh parameter to handle token expiration during long operations
  // Firebase tokens expire after 1 hour, causing silent auth failures mid-session
  const getIdToken = useCallback(async (forceRefresh = false) => {
    if (!currentUser) return null;
    try {
      return await currentUser.getIdToken(forceRefresh);
    } catch (error) {
      console.error('Failed to get ID token:', error);
      return null;
    }
  }, [currentUser]);

  // ✅ FIX: Fetch user profile with retry logic for transient network failures
  const fetchUserProfile = useCallback(async (idToken) => {
    return retryWithBackoff(async () => {
      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.data;
      } else if (response.status === 404) {
        // Profile doesn't exist yet
        return null;
      } else if (response.status === 401) {
        // Unauthorized - token is invalid/expired
        const error = new Error('AUTH_FAILED');
        error.status = 401;
        throw error;
      } else {
        // Throw error to trigger retry
        throw new Error(`HTTP ${response.status}`);
      }
    });
  }, []);

  // ✅ FIX: Create user profile with retry logic for transient network failures
  const createUserProfile = useCallback(async (idToken, profileData) => {
    return retryWithBackoff(async () => {
      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(profileData),
      });

      if (response.ok) {
        const data = await response.json();
        return data.data;
      } else {
        const errorData = await response.json();
        const error = new Error(errorData.message || 'Failed to create profile');
        error.code = errorData.error;
        error.status = response.status;

        // Special handling for 401 - authentication failed
        if (response.status === 401) {
          error.message = 'AUTH_FAILED';
        }

        throw error;
      }
    });
  }, []);

  // Sign up with email and password
  const signup = useCallback(async (email, password, username) => {
    // ✅ FIX: Rate limit signup attempts to prevent abuse
    if (!signupRateLimiter.canProceed()) {
      const waitTime = signupRateLimiter.getTimeUntilNextCall();
      throw new Error(`Too many signup attempts. Please wait ${Math.ceil(waitTime / 1000)} seconds.`);
    }

    // ✅ FIX: Set auth loading state to show loading indicator during signup
    setAuthLoading(true);

    try {
      // Create Firebase auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Get Firebase ID token
      const idToken = await user.getIdToken();

      // Create user profile in MongoDB via Go backend
      const profileData = {
        email: email,
        username: username,
        displayName: username,
        // photoURL will be set when user uploads an avatar later
      };

      try {
        const createdProfile = await createUserProfile(idToken, profileData);
        // Immediately update local state with the created profile
        setUserProfile(createdProfile);
      } catch (error) {
        // If profile creation fails, delete the Firebase auth user to maintain consistency
        await user.delete();
        throw error;
      }

      // Update Firebase auth profile
      await updateProfile(user, { displayName: username });

      // Force reload user to get latest emailVerified status
      await user.reload();
      setCurrentUser(auth.currentUser);

      return user;
    } finally {
      // ✅ FIX: Clear auth loading state even if error occurs
      setAuthLoading(false);
    }
  }, [createUserProfile]);

  // Sign in with email and password
  const login = useCallback(async (email, password) => {
    // ✅ FIX: Rate limit login attempts to prevent brute force attacks
    if (!loginRateLimiter.canProceed()) {
      const waitTime = loginRateLimiter.getTimeUntilNextCall();
      throw new Error(`Too many login attempts. Please wait ${Math.ceil(waitTime / 1000)} seconds.`);
    }

    // ✅ FIX: Set auth loading state to show loading indicator during login
    setAuthLoading(true);

    try {
      return await signInWithEmailAndPassword(auth, email, password);
    } finally {
      // ✅ FIX: Clear auth loading state even if error occurs
      setAuthLoading(false);
    }
  }, []);

  // Sign in with Google
  const signInWithGoogle = useCallback(async () => {
    // ✅ FIX: Rate limit Google sign-in attempts to prevent abuse
    if (!googleSignInRateLimiter.canProceed()) {
      const waitTime = googleSignInRateLimiter.getTimeUntilNextCall();
      throw new Error(`Too many sign-in attempts. Please wait ${Math.ceil(waitTime / 1000)} seconds.`);
    }

    // ✅ FIX: Set auth loading state to show loading indicator during Google sign-in
    setAuthLoading(true);

    try {
    const provider = new GoogleAuthProvider();
    // Ensure clean auth state
    provider.setCustomParameters({
      prompt: 'select_account'
    });

    let result;

    try {
      result = await signInWithPopup(auth, provider);
    } catch (popupError) {
      // User cancelled popup, popup was blocked, or other popup-related errors
      // These are handled gracefully by firebaseErrors.js
      throw popupError;
    }

    const user = result.user;

    try {
      // Get Firebase ID token
      const idToken = await user.getIdToken();

      // Check if user profile exists in MongoDB
      const existingProfile = await fetchUserProfile(idToken);

      if (!existingProfile) {
        // Generate username from email
        const baseUsername = user.email.split('@')[0].toLowerCase();
        const timestamp = Date.now().toString().slice(-4);
        const username = `${baseUsername}${timestamp}`;

        // Create profile for new Google user
        const profileData = {
          email: user.email,
          username: username,
          displayName: user.displayName || username,
        };

        // Only include photoURL if it's a valid HTTP(S) URL
        if (user.photoURL && (user.photoURL.startsWith('http://') || user.photoURL.startsWith('https://'))) {
          profileData.photoURL = user.photoURL;
        }

        const createdProfile = await createUserProfile(idToken, profileData);
        // Immediately update local state with the created profile
        setUserProfile(createdProfile);
      } else {
        // Update local state with existing profile
        setUserProfile(existingProfile);
      }

      return user;
    } catch (error) {
      // ✅ FIX: Delete Firebase user for ANY profile creation failure to prevent orphaned auth state
      // Previously only cleaned up AUTH_FAILED, but network errors/timeouts also leave corrupted state
      try {
        // Delete the Firebase user to prevent partial auth state
        await user.delete();
      } catch (deleteError) {
        // Force sign out as fallback if delete fails
        await signOut(auth);
      }

      // Provide user-friendly error message
      if (error.message === 'AUTH_FAILED') {
        throw new Error('Authentication failed. Please try again or contact support.');
      } else if (error.status === 409) {
        throw new Error('Profile already exists. Please try signing in again.');
      } else {
        throw new Error('Failed to complete sign-in. Please try again.');
      }
    }
    } finally {
      // ✅ FIX: Clear auth loading state even if error occurs
      setAuthLoading(false);
    }
  }, [createUserProfile, fetchUserProfile]);

  // Logout
  const logout = useCallback(() => {
    return signOut(auth);
  }, []);

  // Reset password
  const resetPassword = useCallback((email) => {
    return sendPasswordResetEmail(auth, email);
  }, []);

  // Send email verification
  const sendVerificationEmail = useCallback(async () => {
    if (!currentUser) {
      throw new Error('No user is currently signed in');
    }
    return sendEmailVerification(currentUser);
  }, [currentUser]);

  // Update user profile
  const updateUserProfile = useCallback(async (updates) => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    const idToken = await currentUser.getIdToken();

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.data);

        // If display name changed, update Firebase auth profile
        if (updates.displayName) {
          await updateProfile(currentUser, { displayName: updates.displayName });
        }
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update profile');
      }
    } catch (error) {
      throw error;
    }
  }, [currentUser]);

  useEffect(() => {
    // ✅ FIX: Set mounted flag
    isMountedRef.current = true;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // ✅ FIX: Check if component is mounted before state updates
      if (!isMountedRef.current) return;

      // ✅ PROACTIVE TOKEN REFRESH: Detect email verification and refresh token
      // When user verifies email, the Firebase user object updates but the JWT token
      // still has the old email_verified claim cached. We proactively refresh the token
      // when we detect emailVerified changed from false → true.
      if (user && previousEmailVerifiedRef.current === false && user.emailVerified === true) {
        try {
          // Force token refresh to get updated email_verified claim
          await user.getIdToken(true);
        } catch (error) {
          console.error('Failed to refresh token after email verification:', error);
        }
      }
      
      // Update previous state for next comparison
      previousEmailVerifiedRef.current = user ? user.emailVerified : null;

      setCurrentUser(user);

      if (user) {
        try {
          // ✅ FIX: Force token refresh to get updated email_verified claim
          // Without this, cached tokens still show email_verified: false even after verification
          const idToken = await user.getIdToken(true); // true = force refresh

          // Fetch user profile from MongoDB via Go backend
          const profile = await fetchUserProfile(idToken);

          // ✅ FIX: Check if component is still mounted before state updates
          if (!isMountedRef.current) return;

          if (profile) {
            setUserProfile(profile);
          } else {
            // Profile doesn't exist - only auto-create for Google sign-in (emailVerified=true)
            // Email/password signups handle their own profile creation in signup()
            const isGoogleSignIn = user.emailVerified && user.providerData?.some(p => p.providerId === 'google.com');

            if (isGoogleSignIn && !creatingProfileRef.current) {
              creatingProfileRef.current = true; // Mark as creating

              try {
                // Generate username from email
                const baseUsername = user.email.split('@')[0].toLowerCase();
                const timestamp = Date.now().toString().slice(-4);
                const username = `${baseUsername}${timestamp}`;

                const profileData = {
                  email: user.email,
                  username: username,
                  displayName: user.displayName || username,
                };

                // Only include photoURL if it's a valid HTTP(S) URL
                if (user.photoURL && (user.photoURL.startsWith('http://') || user.photoURL.startsWith('https://'))) {
                  profileData.photoURL = user.photoURL;
                }

                const createdProfile = await createUserProfile(idToken, profileData);

                // ✅ FIX: Check if component is still mounted before state updates
                if (!isMountedRef.current) return;

                setUserProfile(createdProfile);
                creatingProfileRef.current = false; // Reset after success
              } catch (createError) {
                // ✅ FIX: Check if component is still mounted before state updates
                if (!isMountedRef.current) return;

                // If profile already exists (409), fetch it instead of failing
                if (createError.status === 409) {
                  const existingProfile = await fetchUserProfile(idToken);

                  // ✅ FIX: Check if component is still mounted before state updates
                  if (!isMountedRef.current) return;

                  setUserProfile(existingProfile);
                } else if (createError.message === 'AUTH_FAILED') {
                  // Backend authentication failed - delete Firebase user completely
                  try {
                    await user.delete();
                  } catch (deleteError) {
                    // Force sign out as fallback
                    await signOut(auth);
                  }
                  setUserProfile(null);
                  setCurrentUser(null);
                } else {
                  // ✅ FIX: Orphaned user cleanup - profile creation failed, sign out to prevent corrupted state
                  try {
                    await user.delete();
                  } catch (deleteError) {
                    await signOut(auth);
                  }
                  setUserProfile(null);
                  setCurrentUser(null);
                }
                creatingProfileRef.current = false; // Reset after error
              }
            } else if (!user.emailVerified) {
              // Email/password signup - profile will be created by signup() function
              setUserProfile(null);
            } else {
              // ✅ FIX: Orphaned user detection - Firebase user exists but no profile and not creating one
              // This can happen if previous profile creation failed. Clean up to prevent corrupted state.
              try {
                await user.delete();
              } catch (deleteError) {
                await signOut(auth);
              }
              setUserProfile(null);
              setCurrentUser(null);
            }
          }
        } catch (error) {
          // ✅ FIX: Check if component is still mounted before state updates
          if (!isMountedRef.current) return;

          // ✅ FIX: Only delete user on AUTH_FAILED (invalid token), not on network errors
          // Network failures are temporary - user account should remain intact
          if (error.message === 'AUTH_FAILED') {
            try {
              await user.delete();
            } catch (deleteError) {
              await signOut(auth);
            }
            setUserProfile(null);
            setCurrentUser(null);
          } else {
            // Network error - keep user logged in, profile will load on retry
            setUserProfile(null);
          }
        }
      } else {
        setUserProfile(null);
      }

      // ✅ FIX: Check if component is still mounted before state updates
      if (!isMountedRef.current) return;

      setLoading(false);
    });

    // ✅ FIX: Cleanup function to mark component as unmounted
    return () => {
      isMountedRef.current = false;
      unsubscribe();
    };
  }, [createUserProfile, fetchUserProfile]);

  // ✅ PROACTIVE EMAIL VERIFICATION POLLING
  // Poll Firebase every 30 seconds to detect when user verifies email (e.g., on mobile device)
  // This handles cross-device verification where onAuthStateChanged might not immediately update
  useEffect(() => {
    if (!currentUser || currentUser.emailVerified) {
      return; // No polling needed if no user or email already verified
    }

    const pollInterval = setInterval(async () => {
      try {
        // Reload user to get latest emailVerified status from Firebase
        await currentUser.reload();
        
        // If email is now verified, trigger onAuthStateChanged by getting current user
        // This will cause the token refresh logic above to execute
        if (auth.currentUser && auth.currentUser.emailVerified) {
          // Force token refresh immediately
          await auth.currentUser.getIdToken(true);
          
          // Update local state
          setCurrentUser(auth.currentUser);
        }
      } catch (error) {
        console.error('Email verification polling error:', error);
      }
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollInterval);
  }, [currentUser]);

  // ✅ FIX: Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    currentUser,
    userProfile,
    authLoading,
    signup,
    login,
    logout,
    signInWithGoogle,
    resetPassword,
    sendVerificationEmail,
    updateUserProfile,
    getIdToken,
  }), [currentUser, userProfile, authLoading, signup, login, logout, signInWithGoogle, resetPassword, sendVerificationEmail, updateUserProfile, getIdToken]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
